import { Title } from '@gerdau/hefesto/Title';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { sendTagging } from '../../../store/ducks/tagging/actions';
import { getTaggingInformationScreen } from '../../../utils/Tagging';
import { ApplicationState } from '../../../store';
import {
  clearOrdersState,
  loadLongSteelOrders as loadOrdersAction,
  loadLongSteelOrdersFromLocalStorage
} from '../../../store/ducks/long-steel-orders/actions';
import {
  ELongSteelOrdersStatus,
  EOrderTab,
  ILongSteelOrderData,
  IOpenOrderData
} from '../../../store/ducks/long-steel-orders/types';
import { ECustomerTypeView } from '../../../store/ducks/user/types';
import OrdersFilters from './OrdersFilters';
import OrdersSummary from './OrdersSummary';
import OrdersTabs from './OrdersTabs';
import { Container, OrdersHeader } from './styles';

const LongSteelOrders: React.FC = () => {
  const dispatch = useDispatch();

  const ordersState = useSelector(
    (state: ApplicationState) => state.longSteelOrders
  );
  const isManager = useSelector(
    (state: ApplicationState) => state.user.isManager
  );
  const customersState = useSelector(
    (state: ApplicationState) => state.customers
  );
  const customerViewType = useSelector(
    (state: ApplicationState) => state.user.searchCustomerType
  );

  const [orderTab, setOrderTab] = useState<EOrderTab>(
    ordersState.initialInputValues.tab
  );
  const [filteredOrders, setFilteredOrders] = useState<ILongSteelOrderData[]>(
    ordersState.items
  );
  const [receiverId, setReceiverId] = useState('');
  const [receiverBilledId, setReceiverBilledId] = useState('');
  const [emitterIds, setEmitterIds] = useState<string[]>(
    ordersState.initialInputValues.emitterIds || []
  );

  const initOrders = () => {
    dispatch(loadLongSteelOrdersFromLocalStorage());
  };

  const reloadOrders = () => {
    const { states, emitterIds, startDate, endDate } =
      ordersState.initialInputValues;
    loadOrders(states, emitterIds, startDate, endDate);
  };

  const { screenName = '' } = getTaggingInformationScreen('long-steel-orders');

  useEffect(() => {
    dispatch(
      sendTagging({
        page: document.location.pathname,
        event: 'page_view',
        screenName
      })
    );
  }, []);

  useEffect(() => {
    dispatch(
      sendTagging({
        page: document.location.pathname,
        event: 'page_view',
        screenName:
          orderTab === 'open'
            ? 'LG:MeusPedidos:AcosLongos:PedidosAbertos'
            : 'LG:MeusPedidos:AcosLongos:PedidosFaturados'
      })
    );
  }, [orderTab]);

  useEffect(() => {
    if (
      !customersState.loading &&
      !customersState.error &&
      customersState.customers.length > 0
    ) {
      if (!ordersState.initialized && !isManager) {
        initOrders();
      } else if (ordersState.lastLoadTime !== undefined) {
        if (new Date().getTime() - ordersState.lastLoadTime > 3600000) {
          // recarrega as ordens se tiver passado UMA HORA desde o último carregamento
          reloadOrders();
        }
      }
    }
  });

  useEffect(() => {
    if (ordersState.initialInputValues && !ordersState.loading) {
      setOrderTab(ordersState.initialInputValues.tab);
    }
  }, [ordersState.initialInputValues, ordersState.loading]);

  useEffect(() => {
    if (ordersState.items) {
      setFilteredOrders(ordersState.items as IOpenOrderData[]);
    }
  }, [ordersState.items]);

  const loadOrders = (
    states: string[],
    customerIds: string[],
    startDate?: string,
    endDate?: string
  ) => {
    const needToLoadCustomer =
      isManager &&
      !customersState.customers?.find((c) => c.customerId === customerIds[0]);

    dispatch(
      loadOrdersAction(
        states,
        customerIds,
        customerViewType,
        orderTab,
        startDate,
        endDate,
        needToLoadCustomer
      )
    );
  };

  const onChangeOrderTab = (tab: EOrderTab) => {
    dispatch(clearOrdersState(tab, customerViewType));
    setOrderTab(tab);
    setFilteredOrders([]);
  };

  const onCleanFilters = () => {
    ordersState.initialInputValues.orderStatus = ELongSteelOrdersStatus.ALL;
    setFilteredOrders(ordersState.items as IOpenOrderData[]);
  };

  const isFiltered = () => {
    return filteredOrders.length !== ordersState.items.length;
  };

  const isStateZero = (): boolean => {
    if (customerViewType === ECustomerTypeView.EMITTER) {
      return (
        filteredOrders.length === 0 &&
        !isFiltered() &&
        (!ordersState.initialInputValues ||
          ordersState.initialInputValues.emitterIds.length === 0)
      );
    } else {
      return (
        filteredOrders.length === 0 &&
        !isFiltered() &&
        (!ordersState.initialInputValues?.receiverId ||
          !ordersState.initialInputValues?.receiverBilledId)
      );
    }
  };

  return (
    <Container>
      <OrdersHeader>
        <Title level={1}>Meus pedidos</Title>
        {customerViewType === ECustomerTypeView.EMITTER && (
          <span className="subtitle">
            Selecione o estado e o emissor do pedido para exibir os resultados.
          </span>
        )}

        {(customerViewType === ECustomerTypeView.RECEIVER ||
          customerViewType === ECustomerTypeView.FABRICATION_RECEIVER) && (
          <span className="subtitle">
            Selecione o recebedor do pedido para exibir os resultados.
          </span>
        )}

        <OrdersFilters
          orderState={ordersState}
          customerViewType={customerViewType}
          orderTab={orderTab}
          onCleanFilters={onCleanFilters}
          loadOrders={loadOrders}
          isFiltered={isFiltered()}
          setFilteredOrders={setFilteredOrders}
        />
      </OrdersHeader>

      {orderTab !== EOrderTab.BILLED &&
        !ordersState.error &&
        filteredOrders.length > 0 && (
          <OrdersSummary
            filteredOrders={filteredOrders as IOpenOrderData[]}
            loading={ordersState.loading}
          />
        )}
      <OrdersTabs
        customerViewType={customerViewType}
        emitterIds={emitterIds}
        filteredOrders={filteredOrders}
        isStateZero={isStateZero()}
        loading={ordersState.loading}
        loadOrders={loadOrders}
        onChangeTab={onChangeOrderTab}
        onCleanFilters={onCleanFilters}
        receiverBilledId={receiverBilledId}
        receiverId={receiverId}
        selectedTab={orderTab}
        setEmitterIds={setEmitterIds}
        setFilteredOrders={setFilteredOrders}
        setReceiverBilledId={setReceiverBilledId}
        setReceiverId={setReceiverId}
      />
    </Container>
  );
};

export default LongSteelOrders;
